import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, AlertCircle, ChevronLeft, Crown, CheckCircle, Check, Sparkles } from 'lucide-react';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import zxcvbn from 'zxcvbn';
import { Turnstile } from '@marsidev/react-turnstile';
import { supabase } from '../../lib/supabase';
import { sendWelcomeEmail } from '../../lib/email';
import { testEmailConnection, checkEmailConfiguration } from '../../lib/emailTest';
import { getActivePriceDisplay } from '../../stripe-config';
import { GoogleButton } from './GoogleButton';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

interface SignUpProps {
  onToggleSignUp: () => void;
}

interface SignUpData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
}

const initialSignUpData: SignUpData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: ''
};

export function SignUp({ onToggleSignUp }: SignUpProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [signUpData, setSignUpData] = useState<SignUpData>(initialSignUpData);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signupComplete, setSignupComplete] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<any>(null);

  const passwordStrength = signUpData.password ? zxcvbn(signUpData.password) : null;
  const isPasswordStrong = passwordStrength?.score >= 3;

  const handleInputChange = (field: keyof SignUpData, value: string) => {
    setSignUpData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    setError(null);
    
    try {
      console.log('=== EMAIL CONNECTION TEST ===');
      const connectionTest = await testEmailConnection();
      console.log('Connection test result:', connectionTest);
      
      console.log('=== EMAIL CONFIGURATION TEST ===');
      const configTest = await checkEmailConfiguration();
      console.log('Configuration test result:', configTest);
      
      console.log('=== WELCOME EMAIL TEST ===');
      const welcomeTest = await sendWelcomeEmail(
        'test@sermonbuild.com',
        'Test User',
        'Premium'
      );
      console.log('Welcome email test result:', welcomeTest);
      
      alert(`Email tests completed. Check console for details.\n\nConnection: ${connectionTest.success ? 'SUCCESS' : 'FAILED'}\nConfig: ${configTest.success ? 'SUCCESS' : 'FAILED'}\nWelcome: ${welcomeTest.success ? 'SUCCESS' : 'FAILED'}`);
      
    } catch (error) {
      console.error('Email test error:', error);
      setError('Email test failed: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setTestingEmail(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signUpData.email || !signUpData.password || !signUpData.firstName || 
        !signUpData.lastName || !signUpData.phone) {
      setError('Please fill in all required fields');
      return;
    }

    if (!isValidPhoneNumber(signUpData.phone, 'US')) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!isPasswordStrong) {
      setError('Please choose a stronger password');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Format phone number
      const formattedPhone = parsePhoneNumber(signUpData.phone, 'US')?.format('NATIONAL') || signUpData.phone;

      // Create the auth user with metadata
      const { data: signUpResponse, error: signUpError } = await supabase.auth.signUp({
        email: signUpData.email,
        password: signUpData.password,
        options: {
          ...(TURNSTILE_SITE_KEY && captchaToken ? { captchaToken } : {}),
          // Send confirmation email back to /auth so the PKCE exchange lands at the right place
          emailRedirectTo: `${window.location.origin}/auth`,
          data: {
            first_name: signUpData.firstName,
            last_name: signUpData.lastName,
            phone: formattedPhone,
            selectedTier: 'premium', // Always set to premium for trial
            billingInterval: 'monthly'
          }
        }
      });

      if (signUpError) {
        if (signUpError.message.includes('User already registered')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        }
        throw signUpError;
      }

      if (!signUpResponse.user) {
        throw new Error('Failed to create account');
      }

      // Store upgrade info for premium plan
      localStorage.setItem('pendingUpgrade', JSON.stringify({
        tier: 'premium',
        interval: 'monthly'
      }));

      // Send welcome email
      console.log('📧 Attempting to send welcome email...');
      const emailResult = await sendWelcomeEmail(
        signUpData.email,
        signUpData.firstName,
        'Premium'
      );

      console.log('📧 Welcome email result:', emailResult);
      setEmailSent(emailResult.success);

      if (!emailResult.success) {
        console.warn('⚠️ Welcome email could not be sent:', {
          error: emailResult.error,
          email: signUpData.email.substring(0, 3) + '***',
          firstName: signUpData.firstName
        });
        // Continue with signup process even if email fails
      } else {
        console.log('✅ Welcome email sent successfully');
      }

      // Show success message
      setSignupComplete(true);

    } catch (err) {
      console.error('Auth error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during sign up');
      // Reset captcha so user can try again
      setCaptchaToken(null);
      turnstileRef.current?.reset();
      // Clean up any stored data
      localStorage.removeItem('pendingUpgrade');
    } finally {
      setLoading(false);
    }
  };

  const renderPasswordStrength = () => {
    if (!passwordStrength) return null;

    const getStrengthColor = () => {
      switch (passwordStrength.score) {
        case 0: return 'bg-crimson';
        case 1: return 'bg-gold';
        case 2: return 'bg-gold';
        case 3: return 'bg-emerald';
        case 4: return 'bg-emerald';
        default: return 'bg-slate';
      }
    };

    const getStrengthText = () => {
      switch (passwordStrength.score) {
        case 0: return 'Very Weak';
        case 1: return 'Weak';
        case 2: return 'Fair';
        case 3: return 'Strong';
        case 4: return 'Very Strong';
        default: return '';
      }
    };

    return (
      <div className="mt-1">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-2 flex-1 bg-sand/20 rounded-lg overflow-hidden">
            <div 
              className={`h-full ${getStrengthColor()} transition-all duration-300`}
              style={{ width: `${(passwordStrength.score + 1) * 20}%` }}
            />
          </div>
          <span className={`text-xs font-medium ${getStrengthColor().replace('bg-', 'text-')}`}>
            {getStrengthText()}
          </span>
        </div>
        {passwordStrength.feedback.warning && (
          <p className="text-xs text-crimson mt-1">{passwordStrength.feedback.warning}</p>
        )}
        {passwordStrength.feedback.suggestions?.length > 0 && (
          <ul className="text-xs text-slate mt-1 space-y-1">
            {passwordStrength.feedback.suggestions.map((suggestion, index) => (
              <li key={index} className="flex items-start gap-1">
                <span className="text-emerald">•</span>
                {suggestion}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  if (signupComplete) {
    return (
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald/10 text-emerald rounded-full mb-4">
          <CheckCircle className="h-6 w-6" />
        </div>
        <h3 className="text-lg font-semibold text-midnight mb-2">Check your inbox!</h3>
        <p className="text-slate mb-2">
          We sent a confirmation link to <strong>{signUpData.email}</strong>.
        </p>
        <p className="text-slate mb-6">
          Click the link in that email to activate your account and start your free trial. If you don't see it, check your spam folder.
        </p>
        <button
          type="button"
          onClick={onToggleSignUp}
          className="text-electric hover:text-electric/80"
        >
          Return to Sign In
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {currentStep === 1 && (
        <div className="space-y-4">
          <GoogleButton mode="signup" />

          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-sand/30" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-slate">or sign up with email</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate mb-1">First Name</label>
            <input
              type="text"
              value={signUpData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Last Name</label>
            <input
              type="text"
              value={signUpData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Phone Number</label>
            <input
              type="tel"
              value={signUpData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
            />
          </div>
          <button
            type="button"
            onClick={() => setCurrentStep(2)}
            disabled={!signUpData.firstName || !signUpData.lastName || !isValidPhoneNumber(signUpData.phone, 'US')}
            className="w-full py-2 px-4 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
          >
            Continue
          </button>
        </div>
      )}

      {currentStep === 2 && (
        <form onSubmit={handleSignUp} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Email</label>
            <input
              type="email"
              value={signUpData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={signUpData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="w-full px-3 py-2 border border-sand rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-midnight"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {renderPasswordStrength()}
          </div>

          <div className={`${getActivePriceDisplay().isFounders ? 'bg-gradient-to-r from-gold/10 to-electric/10 border border-gold/20' : 'bg-electric/10'} p-4 rounded-lg mb-4`}>
            <div className="flex items-center mb-2">
              {getActivePriceDisplay().isFounders ? (
                <Sparkles className="h-5 w-5 text-gold mr-2" />
              ) : (
                <Crown className="h-5 w-5 text-electric mr-2" />
              )}
              <h3 className="font-medium text-midnight">
                {getActivePriceDisplay().isFounders ? 'Founders Pricing — Limited Time!' : '14-Day Free Premium Trial'}
              </h3>
            </div>
            <p className="text-sm text-slate mb-2">
              {getActivePriceDisplay().isFounders
                ? `Sign up today and get a 14-day free trial. Then lock in Founders Pricing at $${getActivePriceDisplay().monthly}/month (reg. $${getActivePriceDisplay().regularMonthly}) for the lifetime of your subscription:`
                : `Sign up today and get full access to SermonBuild Pro for 14 days. After your trial, continue with a subscription starting at $${getActivePriceDisplay().monthly}/month:`}
            </p>
            <ul className="text-sm text-slate space-y-1">
              <li className="flex items-center">
                <Check className="h-4 w-4 text-emerald mr-2 flex-shrink-0" />
                Unlimited sermons and analyses
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 text-emerald mr-2 flex-shrink-0" />
                Advanced formatting tools
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 text-emerald mr-2 flex-shrink-0" />
                Sermon scheduler and study questions
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 text-emerald mr-2 flex-shrink-0" />
                All premium features included
              </li>
            </ul>
            <p className="text-xs text-slate mt-2">
              No credit card required. You can cancel anytime during your trial.
            </p>
          </div>

          {TURNSTILE_SITE_KEY && (
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={setCaptchaToken}
              onError={() => setCaptchaToken(null)}
              onExpire={() => setCaptchaToken(null)}
              options={{ theme: 'light', size: 'normal' }}
            />
          )}

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="py-2 px-4 text-slate hover:text-midnight"
            >
              <ChevronLeft className="h-4 w-4 inline-block mr-1" />
              Back
            </button>
            <button
              type="submit"
              disabled={loading || !signUpData.email || !signUpData.password || !isPasswordStrong || (!!TURNSTILE_SITE_KEY && !captchaToken)}
              className="py-2 px-4 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin mx-auto" />
              ) : (
                'Start Free Trial'
              )}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="p-4 bg-crimson/10 text-crimson rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <p className="text-sm text-slate text-center">
        Already have an account?{' '}
        <button
          type="button"
          onClick={onToggleSignUp}
          className="text-electric hover:text-electric/80 font-medium"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}